import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { element, getChildrenForSlot } from '../../global/ui-helpers';
import { Labels } from '../../global/labels';
import type {
    UIFooterType,
    UIFooterTypes,
    UIFooterSlot,
    UIFooterSlots,
    UIFooterSlotElements,
} from './ui-footer.type';
import type { NullOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import styles from './ui-footer.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIFooter
 * @element ui-footer
 * @classdesc Represents a class for <code>ui-footer</code> element.
 * Container for legal terms and conditions.
 * @property {boolean} withBar {@attr with-bar} Draws branding bar like ribbon.
 * @property {UIFooterType} type {@attr type} Footer type for different layouts.
 *  {@desc box: For box layout page. Has light text and transparent background.}
 *  {@desc text: For footers with disclaimer text only. Has smaller paddings.}
 * @slot {@name top-left} Top left container of footer. Will take full width if top-right not used.
 * @slot {@name top-right} Top right container of footer. If not used, top-left will take full width.
 * @slot {@name bottom-left} Bottom left container of footer. Will take full width if bottom-right not used.
 * @slot {@name bottom-right} Bottom right container of footer. If not used, bottom-left will take full width.
 * @slot
 * @example
 * <ui-footer></ui-footer>
 */
@Component({ tag: 'ui-footer', styles })
export class UIFooter extends UIElement {
    /**
     * @type {UIFooterTypes}
     */
    static readonly types: UIFooterTypes = {
        Box: 'box',
        Text: 'text',
    };

    /**
     * @type {UIFooterSlots}
     */
    static readonly slots: UIFooterSlots = {
        TopLeft: 'top-left',
        TopRight: 'top-right',
        BottomLeft: 'bottom-left',
        BottomRight: 'bottom-right',
    };

    /**
     * @type {TranslationLabels.Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-footer', {
            footer: 'Footer',
        });
    }

    @Attribute(Boolean) withBar?: boolean;
    @Attribute(String) type?: UIFooterType;

    /**
     * Get visually hidden main heading to increase accessibility.
     * @returns {HTMLHeadingElement}
     * @private
     */
    private getAccessibilityHeading(): HTMLHeadingElement {
        return element`<h2 class="-visually-hidden">${UIFooter.labels.footer}</h2>`;
    }

    /**
     * Extract children for the given slot into a proper wrapper.
     * @param {UIFooterSlot} slot
     * @returns {HTMLElement | null}
     * @private
     */
    private getFormattedSlot(slot: UIFooterSlot): NullOr<HTMLElement> {
        const slotContent =
            getChildrenForSlot(this, slot?.valueOf())?.[0] || null;
        const slotClass = `ui-footer__${slot}-wrapper`;

        return slotContent
            ? element`
                <div class="${slotClass}">
                  ${slotContent}
                </div>
              `
            : null;
    }

    /**
     * Get all content: top slots, divider and bottom slots.
     * @private
     * @returns {{topSlots: HTMLElement | null, divider: HTMLElement | null, bottomSlots: HTMLElement | null}}
     */
    private getAllSlots(): UIFooterSlotElements {
        const topLeftSlot = this.getFormattedSlot(UIFooter.slots.TopLeft);
        const topRightSlot = this.getFormattedSlot(UIFooter.slots.TopRight);
        const bottomLeftSlot = this.getFormattedSlot(UIFooter.slots.BottomLeft);
        const bottomRightSlot = this.getFormattedSlot(
            UIFooter.slots.BottomRight
        );

        const hasTopContent = !!(topLeftSlot || topRightSlot);
        const hasBottomContent = !!(bottomRightSlot || bottomLeftSlot);

        const topSlots = hasTopContent
            ? element`
                <div class="ui-footer__top-wrapper">
                    ${topLeftSlot}
                    ${topRightSlot}
                </div>
            `
            : null;

        const divider =
            hasTopContent && hasBottomContent
                ? element`<ui-divider></ui-divider>`
                : null;

        const bottomSlots = hasBottomContent
            ? element`
                <div class="ui-footer__bottom-wrapper">
                    ${bottomLeftSlot}
                    ${bottomRightSlot}
                </div>
            `
            : null;

        return {
            topSlots: topSlots,
            divider: divider,
            bottomSlots: bottomSlots,
        };
    }

    /**
     * Construct footer layout from slots.
     * @private
     * @returns {HTMLElement | null}
     */
    private getContentWithSlots(): NullOr<HTMLElement> {
        const { topSlots, divider, bottomSlots } = this.getAllSlots();
        const isContentInsideSlots = topSlots || bottomSlots;

        return isContentInsideSlots
            ? element`<div class="ui-footer__grid-wrapper">
                ${this.getAccessibilityHeading()}
                ${topSlots}
                ${divider}
                ${bottomSlots}
            </div>`
            : null;
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.setAttribute('role', 'contentinfo');

        // In case of slots usage, replace the footer inner contents
        const contentWithSlots = this.getContentWithSlots();
        if (contentWithSlots) {
            this.innerHTML = '';
            this.append(contentWithSlots);
            return;
        }
        // If no content in slots, just include accessibility heading before other children
        this.prepend(this.getAccessibilityHeading());
    }
}
